import Trigger from "rc-trigger";
import React, { ReactElement } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Center,
  chakra,
  Divider,
  Flex,
  FlexProps,
  Spacer,
  Text,
} from "@chakra-ui/react";

import {
  APIOption,
  checkSetEqual,
  Dictionary,
  Lang,
  Logger,
  safeCall,
  shallowCopy,
} from "@noli/core";
import { langDescriptions, langStore, switchLangTo, translate, useFlags } from "@noli/business";

import { Editor_Words } from "@/utils/lang/editor";
import { themeStore } from "@/stores/theme";
import { parametersStore, updateParameters } from "@/stores/parameters";
import { ReactComponent as InfoSvg } from "@/assets/icons/info.svg";
import Icon from "@/components/Icon";
import Link from "@/components/Link";
import Select from "@/components/Select";
import SchemaProperty from "@/components/Property/SchemaProperty";
import Property from "@/components/Property";
import { toast, useOwnToast } from "@/utils/misc";
import { loginStore } from "@/modules/login/store";
import { runInAction } from "mobx";

const InputField: React.FC<{
  identifier: string;
  value: string;
  setValue: (value: string) => void;
}> = ({ identifier, value, setValue }) => (
  <Property
    identifier={identifier}
    w="70%"
    noTitle
    inputPadding="10px"
    inputProps={{
      textAlign: "right",
    }}
    value={value}
    setValue={(value) => setValue(value)}
  />
);

const SelectField: React.FC<{
  identifier: string;
  lang: Lang;
  value: string;
  values: string[];
  setValue: (value: string) => void;
}> = ({ identifier, lang, value, values, setValue }) => (
  <SchemaProperty
    w="68%"
    px="12px"
    pr="8px"
    identifier={identifier}
    lang={lang}
    noTitle
    apiKey={identifier}
    apiOption={new APIOption("choice", value, { choices: values })}
    value={value}
    setValue={() => setValue}
  />
);

const CaptionedField: React.FC<
  {
    caption: string;
    captionColor: string;
    inputField: ReactElement;
    captionW?: string;
  } & FlexProps
> = ({ caption, captionColor, inputField, captionW, ...others }) => {
  return (
    <Flex w="100%" h="32px" mb="8px" {...others}>
      <Flex w={captionW ?? "100px"} align="center" color={captionColor} fontSize="12px">
        {caption}
      </Flex>
      <Spacer />
      {inputField}
    </Flex>
  );
};

const CaptionedInput: React.FC<
  {
    identifier: string;
    caption: string;
    captionColor: string;
    value: string;
    setValue: (value: string) => void;
    captionW?: string;
  } & FlexProps
> = ({ identifier, caption, captionColor, value, setValue, captionW, ...others }) => {
  return (
    <CaptionedField
      caption={caption}
      captionColor={captionColor}
      inputField={<InputField identifier={identifier} value={value} setValue={setValue} />}
      captionW={captionW}
      {...others}
    />
  );
};

const CaptionedSelect: React.FC<
  {
    identifier: string;
    lang: Lang;
    caption: string;
    captionColor: string;
    value: string;
    values: string[];
    setValue: (value: string) => void;
    captionW?: string;
  } & FlexProps
> = ({ identifier, lang, caption, captionColor, value, values, setValue, captionW, ...others }) => {
  return (
    <CaptionedField
      caption={caption}
      captionColor={captionColor}
      inputField={
        <SelectField
          identifier={identifier}
          lang={lang}
          value={value}
          values={values}
          setValue={setValue}
        />
      }
      captionW={captionW}
      {...others}
    />
  );
};

function SettingsPanel() {
  const t = useOwnToast();
  const currentLang = langStore.tgt;
  const supportedLangs = Object.keys(langDescriptions);
  const { useGuidelineSystem, setGuidelineSystem } = useFlags();
  const {
    useLocalServer,
    localServerHost,
    useLocalModel,
    localModelVersion,
    localModelName,
    availableModelVersions,
    availableModelNames,
    localModelRoot,
    tokenRoot,
  } = parametersStore;
  const { panelBg, captionColor, separatorColor } = themeStore.styles;

  function _onPost(
    uri: string,
    data: Dictionary<any>,
    callback: () => void,
    submit_word: Editor_Words,
    success_word: Editor_Words,
    error_word: Editor_Words,
  ): void {
    toast(t, "info", translate(submit_word, currentLang));
    safeCall(
      async () => {
        const res = await fetch(`${parametersStore.creatorHost}${uri}`, {
          method: "POST",
          headers: loginStore.jsonPostReqHeaders,
          body: JSON.stringify(data),
        });
        if (!res.ok) throw Error(await res.text());
        const json = await res.json();
        if (!json.success) throw Error(json.reason);
        callback();
        toast(t, "success", translate(success_word, currentLang));
      },
      {
        success: async () => void 0,
        failed: async (e) => toast(t, "error", `${translate(error_word, currentLang)} | ${e}`),
      },
    );
  }

  function _refreshAvailable(): void {
    safeCall(
      async () => {
        const res = await fetch(`${parametersStore.creatorHost}/available_versions`, {
          method: "POST",
          headers: loginStore.jsonPostReqHeaders,
        });
        if (!res.ok) throw Error(await res.text());
        const available = (await res.json()).versions as string[] | undefined;
        const availableJson = JSON.stringify(available);
        Logger.debug(`fetched available versions: ${availableJson}`);
        if (!available) throw Error(`illegal versions: ${JSON.stringify(available)}`);
        if (available.length === availableModelVersions.length) {
          if (checkSetEqual(new Set(available), new Set(availableModelVersions))) {
            return;
          }
        }
        runInAction(() => {
          parametersStore.localModelVersion = available.includes("sd_v1.5")
            ? "sd_v1.5"
            : available[0];
          parametersStore.availableModelVersions = available;
        });
      },
      {
        success: async () => void 0,
        failed: async (e) => Logger.error(`failed to update available versions | ${e}`),
      },
    );
    safeCall(
      async () => {
        const res = await fetch(`${parametersStore.creatorHost}/available_models`, {
          method: "POST",
          headers: loginStore.jsonPostReqHeaders,
        });
        if (!res.ok) throw Error(await res.text());
        const available = (await res.json()).models as string[] | undefined;
        const availableJson = JSON.stringify(available);
        Logger.debug(`fetched available models: ${availableJson}`);
        if (!available) throw Error(`illegal models: ${JSON.stringify(available)}`);
        if (available.length === availableModelNames.length) {
          if (checkSetEqual(new Set(available), new Set(availableModelNames))) {
            return;
          }
        }
        runInAction(() => {
          parametersStore.localModelName = available[0];
          parametersStore.availableModelNames = available;
        });
      },
      {
        success: async () => void 0,
        failed: async (e) => Logger.error(`failed to update available models | ${e}`),
      },
    );
  }

  function onSwitch(): void {
    _onPost(
      "/switch",
      {
        key: localModelVersion,
        model: localModelName,
      },
      () => {
        const activated = shallowCopy(parametersStore.activatedLocalModels);
        activated[localModelVersion] = localModelName;
        updateParameters("activatedLocalModels", activated);
      },
      Editor_Words["switch-local-model-submit-message"],
      Editor_Words["switch-local-model-success-message"],
      Editor_Words["switch-local-model-error-message"],
    );
  }

  function onReset(): void {
    _onPost(
      "/reset",
      { version: localModelVersion },
      () => {
        const activated = shallowCopy(parametersStore.activatedLocalModels);
        delete activated[localModelVersion];
        updateParameters("activatedLocalModels", activated);
      },
      Editor_Words["reset-local-model-submit-message"],
      Editor_Words["reset-local-model-success-message"],
      Editor_Words["reset-local-model-error-message"],
    );
  }

  function onSwitchRoot(): void {
    _onPost(
      "/switch_root",
      { root: localModelRoot },
      () => _refreshAvailable(),
      Editor_Words["switch-local-model-root-submit-message"],
      Editor_Words["switch-local-model-root-success-message"],
      Editor_Words["switch-local-model-root-error-message"],
    );
  }

  function onRefreshTokens(): void {
    _onPost(
      "/inject_tokens",
      { root: tokenRoot },
      () => void 0,
      Editor_Words["refresh-tokens-submit-message"],
      Editor_Words["refresh-tokens-success-message"],
      Editor_Words["refresh-tokens-error-message"],
    );
  }

  if (useLocalServer && useLocalModel) {
    _refreshAvailable();
    if (!localModelRoot) {
      safeCall(
        async () => {
          const res = await fetch(`${parametersStore.creatorHost}/model_root`, {
            method: "POST",
            headers: loginStore.jsonPostReqHeaders,
          });
          if (!res.ok) throw Error(await res.text());
          const modelRoot = (await res.json()).root as string | undefined;
          Logger.debug(`fetched model root: ${modelRoot}`);
          if (!modelRoot) throw Error("failed to fetch model root");
          updateParameters("localModelRoot", modelRoot);
        },
        {
          success: async () => void 0,
          failed: async (e) => Logger.error(`failed to update model root | ${e}`),
        },
      );
    }
  }

  return (
    <Flex flex={1} h="100%" direction="column">
      <Flex w="100%" direction="column" align="start" py="24px">
        <Text
          px="16px"
          mb="8px"
          color="text.title"
          fontSize="12px"
          fontWeight={700}
          userSelect="none"
        >
          语言 / Language
        </Text>
        <Center w="100%">
          <Box w="90%" mt="8px">
            <Select
              flex={1}
              w="100%"
              h="32px"
              pl="12px"
              iconProps={{ pr: "8px" }}
              value={langDescriptions[currentLang]}
              options={supportedLangs}
              optionConverter={(lang: string) => langDescriptions[lang as Lang]}
              onOptionClick={(lang: string) => switchLangTo(lang as Lang)}
            />
          </Box>
        </Center>
      </Flex>
      <Flex w="100%" direction="column" align="start" py="24px" borderTop="1px solid #4d4d4d">
        <Text
          px="16px"
          mb="8px"
          color="text.title"
          fontSize="12px"
          fontWeight={700}
          userSelect="none"
        >
          {translate("advanced-settings", currentLang)}
        </Text>

        <SchemaProperty
          identifier="use-guideline-and-magnet"
          pl="16px"
          pr="20px"
          mt="24px"
          w="100%"
          lang={currentLang}
          apiKey="use-guideline-and-magnet"
          apiOption={new APIOption("bool", useGuidelineSystem)}
          value={useGuidelineSystem}
          setValue={() => (value) => setGuidelineSystem(value)}
        />
        <SchemaProperty
          identifier="use-local-server"
          pl="16px"
          pr="20px"
          mt="28px"
          w="100%"
          lang={currentLang}
          apiKey={Editor_Words["use-local-server"]}
          apiOption={new APIOption("bool", useLocalServer)}
          value={useLocalServer}
          setValue={() => (value: boolean) => updateParameters("useLocalServer", value)}
          booleanTooltip={
            <Trigger
              action={["hover"]}
              popup={
                <Flex
                  w="120px"
                  bg={panelBg}
                  flexDir="column"
                  fontSize="14px"
                  lineHeight="22px"
                  pl="8px"
                  py="4px"
                  color={captionColor}
                >
                  <Link
                    text="Google Colab"
                    url="https://colab.research.google.com/github/carefree0910/carefree-creator/blob/dev/tests/server.ipynb"
                  />
                  <Link
                    text="中文版 Colab"
                    url="https://colab.research.google.com/github/carefree0910/carefree-creator/blob/dev/tests/server_zh.ipynb"
                  />
                </Flex>
              }
              popupAlign={{
                points: ["cl", "cr"],
                offset: [0, 0],
              }}
            >
              <chakra.div ml="4px">
                <Icon style={{ fontSize: "12px" }} svg={InfoSvg} />
              </chakra.div>
            </Trigger>
          }
        />
        {useLocalServer ? (
          <Flex w="100%" px="16px" mt="24px" direction="column">
            <CaptionedInput
              identifier="local-server-host"
              caption={translate(Editor_Words["local-server-host"], currentLang)}
              captionColor={captionColor}
              value={localServerHost}
              setValue={(value) => updateParameters("localServerHost", value)}
            />
          </Flex>
        ) : null}
        {useLocalServer ? (
          <SchemaProperty
            identifier="use-local-model"
            pl="16px"
            pr="20px"
            mt="28px"
            lang={currentLang}
            apiKey={Editor_Words["use-local-model"]}
            apiOption={new APIOption("bool", useLocalModel)}
            value={useLocalModel}
            setValue={() => (value) => updateParameters("useLocalModel", value)}
          />
        ) : null}
        {useLocalServer && useLocalModel ? (
          <Flex w="100%" px="16px" mt="24px" direction="column">
            <CaptionedSelect
              pl="8px"
              identifier="local-model-version"
              lang={currentLang}
              caption={translate(Editor_Words["local-model-version"], currentLang)}
              captionColor={captionColor}
              value={localModelVersion}
              values={availableModelVersions}
              setValue={(value) => updateParameters("localModelVersion", value)}
            />
            <CaptionedSelect
              pl="8px"
              identifier="local-model-name"
              lang={currentLang}
              caption={translate(Editor_Words["local-model-name"], currentLang)}
              captionColor={captionColor}
              value={localModelName}
              values={availableModelNames}
              setValue={(value) => updateParameters("localModelName", value)}
            />
            <Flex>
              <Button w="75%" mt="12px" onClick={onSwitch}>
                {translate(Editor_Words["switch-local-model"], currentLang)}
              </Button>
              <Spacer />
              <Button w="20%" mt="12px" onClick={onReset} variant="outline">
                {translate(Editor_Words["reset-local-model"], currentLang)}
              </Button>
            </Flex>
          </Flex>
        ) : null}
        {useLocalServer && useLocalModel ? (
          <Flex
            w="100%"
            px="16px"
            mt="24px"
            borderTop="1px"
            borderColor={separatorColor}
            direction="column"
          >
            <CaptionedInput
              mt="16px"
              identifier="local-model-root"
              caption={translate(Editor_Words["local-model-root"], currentLang)}
              captionColor={captionColor}
              value={localModelRoot}
              setValue={(value) => updateParameters("localModelRoot", value)}
              captionW="120px"
            />
            <Button w="100%" mt="12px" onClick={onSwitchRoot}>
              {translate(Editor_Words["switch-local-model-root"], currentLang)}
            </Button>
          </Flex>
        ) : null}
        {useLocalServer ? (
          <Flex
            w="100%"
            px="16px"
            mt="24px"
            borderTop="1px"
            borderColor={separatorColor}
            direction="column"
          >
            <CaptionedInput
              mt="16px"
              identifier="tokens-root"
              caption={translate(Editor_Words["tokens-root"], currentLang)}
              captionColor={captionColor}
              value={tokenRoot}
              setValue={(value) => updateParameters("tokenRoot", value)}
              captionW="120px"
            />
            <Button w="100%" mt="12px" onClick={onRefreshTokens}>
              {translate(Editor_Words["refresh-tokens"], currentLang)}
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
}

export default observer(SettingsPanel);
